/**
 * Lista de campañas del WhatsApp oficial (`/activa/oficial/campanas`).
 *
 * Los filtros son en el cliente: una marca tiene decenas de campañas, no miles, y «En curso» son
 * cuatro estados que el backend no filtra juntos. Con alguna enviando, la lista se refresca cada
 * 10 s. Si el pedido falla se dice («No pudimos traer…»), nunca una lista vacía que parezca «no hay».
 *
 * Una campaña confirmada en otro modo que el del servidor (`otroModo`, C1: la de prueba del
 * simulado con el servidor ya en twilio) no está «En curso»: no va a salir nunca. Se ve en «Todas»,
 * con su chip y el corte en ámbar.
 *
 * Una campaña que se repite lleva el chip «Se repite», y una repetición dice de cuál es (28/10/2026, `RepeticionCampana`).
 *
 * El equipo (no una persona de un cliente) duplica cualquier campaña (abre el asistente en el borrador nuevo) y borra un
 * borrador desde la fila, con la confirmación ahí mismo (`AccionesCampana`). Del asistente o del detalle, borrar vuelve
 * acá con el aviso en `location.state`: se muestra una vez y se saca del historial.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, Plus, RefreshCw, Send, Zap } from "lucide-react";
import type { CampanaResumen, EstadoCampanaOficial } from "../types";
import { oficialApi } from "../api";
import { usePolling } from "../hooks/usePolling";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { ESPERA, motivoCorteVisible, textoCorte } from "../logic/textos";
import { usd } from "../logic/formato";
import { ChipEstadoCampanaOficial } from "../components/ChipEstadoCampanaOficial";
import { BorrarBorrador, BotonDuplicarCampana } from "../components/AccionesCampana";
import { ChipSeRepite } from "../components/RepeticionCampana";
import { RUTA_DISPARADORES } from "../logic/disparadores";
import { fechaHora, num, pct, plural } from "../../campana-nueva/utils/format";
import { SoloEquipo } from "../../campana-nueva/components/SoloEquipo";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { Columns3 } from "lucide-react";

const B = "/activa/oficial/campanas";

type Filtro = "todas" | "curso" | "borradores" | "terminadas";
const FILTROS: { key: Filtro; label: string; estados: EstadoCampanaOficial[] | null; sinOtroModo?: boolean }[] = [
  { key: "todas", label: "Todas", estados: null },
  { key: "curso", label: "En curso", estados: ["enviando", "programada", "pausada", "pausada_auto"], sinOtroModo: true },
  { key: "borradores", label: "Borradores", estados: ["borrador"] },
  { key: "terminadas", label: "Terminadas", estados: ["finalizada", "cancelada"] },
];

/**
 * Un borrador se retoma en el asistente SIN `?paso=`: el resumen de la lista no trae el mapeo, y
 * adivinar el paso podía mandar a «Prueba» una campaña con variables sin completar. El asistente
 * calcula el paso más lejano con la campaña entera (`maxPasoAlcanzable`).
 */
function rutaCampana(c: CampanaResumen): string {
  const id = encodeURIComponent(c.id);
  return c.estado === "borrador" ? `${B}/${id}/editar` : `${B}/${id}`;
}

export default function CampanasOficialListaPage() {
  const [items, setItems] = useState<CampanaResumen[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const location = useLocation();
  const navigate = useNavigate();
  // El aviso con el que vuelve un borrado desde el asistente o el detalle: se lee una vez y se saca del historial.
  const [aviso, setAviso] = useState<string>(() => String((location.state as { aviso?: string } | null)?.aviso || ""));
  useEffect(() => {
    if ((location.state as { aviso?: string } | null)?.aviso) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    // Sólo al montar, como en el detalle de la campaña.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** `lanzar`: en el refresco periódico un fallo se lanza para que `usePolling` lo cuente. */
  const cargar = useCallback(async (lanzar = false) => {
    try {
      const l = leerRespuesta<CampanaResumen[]>(await oficialApi.campanas());
      if (!l.ok) throw Object.assign(new Error(l.error), { status: l.status, body: { error: l.error, code: l.code } });
      setItems(Array.isArray(l.data) ? l.data : []);
      setError("");
    } catch (e) {
      setError(textoError(leerError(e)));
      if (lanzar) throw e;
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  const hayEnviando = (items || []).some((c) => c.estado === "enviando");
  const poll = usePolling(() => cargar(true), hayEnviando ? 10000 : null, []);

  const alBorrar = useCallback((c: CampanaResumen) => {
    setItems((prev) => (prev || []).filter((x) => x.id !== c.id));
    setAviso(`Se borró el borrador «${c.nombre}».`);
  }, []);

  const visibles = useMemo(() => {
    const f = FILTROS.find((x) => x.key === filtro);
    return (items || []).filter((c) => (!f?.estados || f.estados.includes(c.estado)) && !(f?.sinOtroModo && c.otroModo === true));
  }, [items, filtro]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Campañas</h2>
          {/* «Comparar campañas» la ve también un cliente, como la lista */}
          <Link to={`${B}/comparar`} className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
            <Columns3 size={14} /> Comparar campañas
          </Link>
        </div>
        {/* Armar y lanzar campañas es del equipo de Deenex: un cliente las ve. Los disparadores (29/10/2026) también. */}
        <SoloEquipo>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={RUTA_DISPARADORES} className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2">
              <Zap size={16} /> Disparadores
            </Link>
            <Link to={`${B}/nueva`} className="h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2">
              <Plus size={16} /> Nueva campaña
            </Link>
          </div>
        </SoloEquipo>
      </div>

      <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1 w-fit max-w-full overflow-x-auto" role="group" aria-label="Filtrar campañas">
        {FILTROS.map((f) => (
          <button key={f.key} type="button" onClick={() => setFiltro(f.key)} aria-pressed={filtro === f.key}
            className={`h-8 px-3 rounded-lg text-xs font-semibold whitespace-nowrap ${filtro === f.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {aviso && (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" role="status">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> <span>{aviso}</span>
        </div>
      )}

      {poll.detenido && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span>Dejamos de actualizar porque el servidor no responde.</span>
          <button type="button" onClick={poll.reintentar} className="font-semibold underline">Reintentar</button>
        </div>
      )}

      {cargando && !items ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      ) : error && !items ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700" role="alert">
          <p className="font-semibold">No pudimos traer las campañas.</p>
          <p className="mt-1">{error}</p>
          <button type="button" onClick={() => { setCargando(true); void cargar(); }} className="mt-3 inline-flex items-center gap-1.5 font-semibold underline">
            <RefreshCw size={13} /> Reintentar
          </button>
        </div>
      ) : !items?.length ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <Send size={22} className="mx-auto text-slate-300" />
          <SoloEquipo siNo={<p className="mt-3 text-sm text-slate-600">Todavía no hay campañas. Las arma el equipo de Deenex: cuando haya una, la vas a ver acá.</p>}>
            {/* las cuatro pestañas de la Audiencia del asistente, en su orden */}
            <p className="mt-3 text-sm text-slate-600">
              Todavía no hay campañas. Elegí una plantilla (o creala acá mismo) y a quién mandársela: el Público de Activa (todo o
              un segmento), una campaña preparada en Nueva campaña, un público de tu base o un Excel que subas.
            </p>
            <Link to={`${B}/nueva`} className="mt-4 h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2">
              <Plus size={16} /> Nueva campaña
            </Link>
          </SoloEquipo>
        </div>
      ) : !visibles.length ? (
        <p className="py-8 text-center text-sm text-slate-500">Ninguna campaña con ese filtro.</p>
      ) : (
        <ul className="space-y-3">
          {error && <li className="text-xs text-amber-700">{error} Se muestra lo último que trajimos.</li>}
          {visibles.map((c) => <FilaCampana key={c.id} c={c} onBorrada={() => alBorrar(c)} />)}
        </ul>
      )}
    </div>
  );
}

function FilaCampana({ c, onBorrada }: { c: CampanaResumen; onBorrada: () => void }) {
  const equipo = useEsEquipoDeenex();
  const k = c.contadores || { total: 0, enviados: 0, entregados: 0, leidos: 0, fallidos: 0, enCola: 0 };
  // `total` es lo asignado al envío: ya viene sin los `control` de campañas viejas (contadoresDesdeConteos).
  const aEnviar = Math.max(0, k.total);
  const otroModo = c.otroModo === true;
  const corte = c.estado === "pausada_auto" || otroModo ? textoCorte(motivoCorteVisible(c), c) : null;
  return (
    <li className="rounded-2xl border border-slate-100 bg-white shadow-sm hover:border-slate-200 transition-colors">
      <Link to={rutaCampana(c)} className="block rounded-2xl p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-900 truncate">{c.nombre}</p>
              <ChipEstadoCampanaOficial estado={c.estado} />
              <ChipSeRepite repeticion={c.repeticion} />
              {c.simulado && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">simulada</span>}
            </div>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {c.plantilla?.nombreVisible || "Sin plantilla"} · {c.audiencia?.nombre ? `${c.audiencia.tipo === "base" ? "Público " : ""}${c.audiencia.nombre}` : c.audiencia?.tipo === "publico" ? "Público de Activa" : "sin audiencia"}
            </p>
            {/* una repetición: de cuál es (sólo el texto: la fila entera ya es un link; los motivos están en el detalle) */}
            {c.repeticionDe && (
              <p className="text-xs text-slate-500 mt-0.5">
                Repetición {c.repeticionDe.n} de {c.repeticionDe.nombre}
                {c.estado === "borrador" && c.repeticionDe.decision === "borrador" && <span className="text-amber-700 font-medium"> · quedó en borrador: revisala y confirmala</span>}
              </p>
            )}
            {c.repeticion?.ultima && !c.repeticion.ultima.salio && c.repeticion.ultima.estado === "borrador" && (
              <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle size={11} /> La repetición «{c.repeticion.ultima.nombre}» quedó en borrador</p>
            )}
            {c.estado === "programada" && c.programadaPara && <p className="text-xs text-indigo-600 mt-1">Sale el {fechaHora(c.programadaPara)}</p>}
            {c.espera && <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><Clock size={11} /> {ESPERA[c.espera]?.titulo}</p>}
            {corte && c.estado !== "finalizada" && c.estado !== "cancelada" && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${otroModo ? "text-amber-700" : "text-red-600"}`}><AlertTriangle size={11} /> {corte.titulo}</p>
            )}
          </div>
          <div className="text-right text-xs text-slate-500 flex-shrink-0">
            {c.estado !== "borrador" && (
              <>
                <p><strong className="text-slate-900">{num(k.enviados)}</strong> de {num(aEnviar)} · {num(k.entregados)} {plural(k.entregados, "entregado", "entregados")}</p>
                <div className="mt-1.5 h-1.5 w-40 max-w-full rounded-full bg-violet-100 overflow-hidden ml-auto">
                  <div className="h-full bg-violet-600 rounded-full" style={{ width: `${Math.min(100, pct(k.enviados, aEnviar))}%` }} />
                </div>
              </>
            )}
            {c.costoEstimado && <p className="mt-1">hasta {usd(c.costoEstimado.clienteMaximoUSD)}</p>}
          </div>
        </div>
      </Link>
      {/* Fuera del link: un botón adentro de un <a> no es HTML válido, y el clic abriría la campaña. */}
      {equipo && (
        <div className="flex items-start gap-4 flex-wrap border-t border-slate-50 px-4 py-2">
          {/* un seguimiento no se duplica (28/10/2026): su copia saldría a toda la audiencia de la original */}
          {!c.seguimientoDe && <BotonDuplicarCampana id={c.id} estilo="enlace" />}
          {c.estado === "borrador" && <BorrarBorrador id={c.id} nombre={c.nombre} onBorrada={onBorrada} estilo="enlace" />}
        </div>
      )}
    </li>
  );
}
